import React, { useCallback, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Superscript from '@tiptap/extension-superscript';
import Subscript from '@tiptap/extension-subscript';
import { Extension } from '@tiptap/core';
import { uploadFile } from '@/lib/api';
import { toast } from 'sonner';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, List, ListOrdered, Quote, Code2,
  Link as LinkIcon, Image as ImageIcon, Undo2, Redo2, Minus,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Palette, Highlighter, Table as TableIcon, ListChecks,
  Superscript as SuperIcon, Subscript as SubIcon, Type,
  ChevronDown, Rows3, Columns3, Trash2, Eraser, Upload, Link2,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// ────────────────────────────────────────────────────────────
// Custom FontSize extension
//   TipTap doesn't ship a stock font-size mark, so we extend TextStyle's
//   attributes to accept an inline `font-size` CSS value. This keeps HTML
//   output portable (renders as <span style="font-size: …">) and lets
//   us define chainable `setFontSize` / `unsetFontSize` commands.
// ────────────────────────────────────────────────────────────
const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() {
    return { types: ['textStyle'] };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (el) => el.style.fontSize || null,
            renderHTML: (attrs) => {
              if (!attrs.fontSize) return {};
              return { style: `font-size: ${attrs.fontSize}` };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize: (size) => ({ chain }) => chain().setMark('textStyle', { fontSize: size }).run(),
      unsetFontSize: () => ({ chain }) => chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
    };
  },
});

// Utility toolbar primitives ------------------------------------------------
const BtnGroup = ({ children }) => (
  <div className='inline-flex items-center gap-0.5 border-r border-border last:border-r-0 pr-1 mr-1'>
    {children}
  </div>
);

const Btn = ({ onClick, active, disabled, title, children, testid }) => (
  <button
    type='button'
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    data-testid={testid}
    className={`h-8 w-8 rounded-md inline-flex items-center justify-center hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed transition-colors ${active ? 'bg-secondary text-primary' : 'text-muted-foreground'}`}
  >
    {children}
  </button>
);

// A compact colour swatch grid — reused by text-colour and highlight pickers.
const PALETTE = [
  { name: 'Default', color: null },
  { name: 'Slate', color: '#334155' },
  { name: 'Grey', color: '#6b7280' },
  { name: 'Red', color: '#dc2626' },
  { name: 'Orange', color: '#ea580c' },
  { name: 'Amber', color: '#d97706' },
  { name: 'Green', color: '#16a34a' },
  { name: 'Teal', color: '#0d9488' },
  { name: 'Blue', color: '#2563eb' },
  { name: 'Indigo', color: '#4f46e5' },
  { name: 'Purple', color: '#9333ea' },
  { name: 'Pink', color: '#db2777' },
];

const HIGHLIGHT_PALETTE = [
  { name: 'None', color: null },
  { name: 'Yellow', color: '#fef08a' },
  { name: 'Green', color: '#bbf7d0' },
  { name: 'Blue', color: '#bfdbfe' },
  { name: 'Pink', color: '#fbcfe8' },
  { name: 'Orange', color: '#fed7aa' },
  { name: 'Purple', color: '#e9d5ff' },
];

const FONT_SIZES = [
  { label: 'Small', value: '0.875em' },
  { label: 'Normal', value: null },
  { label: 'Large', value: '1.125em' },
  { label: 'XL', value: '1.35em' },
  { label: '2XL', value: '1.6em' },
];

const HEADINGS = [
  { label: 'Paragraph', level: 0 },
  { label: 'Heading 1', level: 1 },
  { label: 'Heading 2', level: 2 },
  { label: 'Heading 3', level: 3 },
  { label: 'Heading 4', level: 4 },
  { label: 'Heading 5', level: 5 },
];

const ColorSwatchGrid = ({ palette, onPick, current }) => (
  <div className='grid grid-cols-6 gap-1 p-1'>
    {palette.map((c) => (
      <button
        type='button'
        key={c.name}
        onClick={() => onPick(c.color)}
        title={c.name}
        aria-label={c.name}
        className={`h-6 w-6 rounded border transition-all hover:scale-110 ${current === c.color ? 'ring-2 ring-primary ring-offset-1' : 'border-border'}`}
        style={{ background: c.color || 'transparent', backgroundImage: !c.color ? 'linear-gradient(45deg, #f3f4f6 25%, transparent 25%, transparent 75%, #f3f4f6 75%, #f3f4f6), linear-gradient(45deg, #f3f4f6 25%, transparent 25%, transparent 75%, #f3f4f6 75%, #f3f4f6)' : undefined, backgroundSize: !c.color ? '6px 6px' : undefined, backgroundPosition: !c.color ? '0 0, 3px 3px' : undefined }}
      />
    ))}
  </div>
);

// Convert an image filename into a reasonable default alt text.
//   "used-cnc-lathe_01.JPG"  ->  "Used Cnc Lathe 01"
//   "IMG_2311 (2).png"       ->  "IMG 2311"
// Strips extension, common separators, tracker suffixes, and title-cases
// the remainder so staff always start from something meaningful rather
// than staring at an empty box.
const altFromFilename = (name) => {
  if (!name) return '';
  const stem = String(name).replace(/\.[a-z0-9]+$/i, '');
  const cleaned = stem
    .replace(/[_\-\.]+/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .replace(/\(\d+\)/g, '')
    .trim();
  if (!cleaned) return '';
  return cleaned
    .split(' ')
    .filter(Boolean)
    .map(w => (/^[a-z]/.test(w) ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
};

// Custom Image extension that ensures the `title` attribute survives
// serialisation (used to store optional caption / tooltip text). The
// stock @tiptap/extension-image supports title, but we override to make
// intent explicit and future-proof any prosemirror serialisation quirks.
const ImageWithTitle = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      title: {
        default: null,
        parseHTML: (el) => el.getAttribute('title'),
        renderHTML: (attrs) => (attrs.title ? { title: attrs.title } : {}),
      },
    };
  },
});

// Size of the hover/click table-size picker grid. Larger tables are still
// possible via the custom rows/cols inputs below the grid.
const TABLE_GRID_ROWS = 8;
const TABLE_GRID_COLS = 10;

const RichTextEditor = ({ value, onChange }) => {
  // See detailed comment below the useEditor hook — this ref prevents an
  // infinite feedback loop between parent state and the editor.
  const lastValueRef = React.useRef(value || '');
  const [_, forceUpdate] = useState(0);

  // Image insert / edit dialog state.
  //   mode='insert' — new image, we own the src (via upload or paste URL)
  //   mode='edit'   — existing image node in the doc; `pos` is its ProseMirror
  //                    position so we can updateAttributes on save.
  // Table insert menu state — the size grid is a hover/click picker (like
  // Word/Google Docs) so staff can insert any R×C table, not a fixed 3×3.
  const [tableMenuOpen, setTableMenuOpen] = useState(false);
  const [tblHover, setTblHover] = useState({ rows: 0, cols: 0 });
  const [tblHeader, setTblHeader] = useState(true);
  const [tblCustom, setTblCustom] = useState({ rows: '3', cols: '3' });

  const [imgDlg, setImgDlg] = useState({
    open: false,
    mode: 'insert', // 'insert' | 'edit'
    src: '',
    alt: '',
    title: '', // caption / tooltip
    pos: null,
    uploading: false,
  });

  const openImageDialogForInsert = useCallback(() => {
    setImgDlg({ open: true, mode: 'insert', src: '', alt: '', title: '', pos: null, uploading: false });
  }, []);

  const openImageDialogForEdit = useCallback((attrs, pos) => {
    setImgDlg({
      open: true,
      mode: 'edit',
      src: attrs.src || '',
      alt: attrs.alt || '',
      title: attrs.title || '',
      pos,
      uploading: false,
    });
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Enable H1-H5. H6 excluded to discourage overly deep hierarchies
        // (also matches Google's typical rendered depth on SERPs).
        heading: { levels: [1, 2, 3, 4, 5] },
        // TipTap v3's StarterKit bundles Link + Underline by default. We
        // configure them explicitly below (Link needs custom target/rel;
        // Underline just needs to be there for keyboard shortcuts) so we
        // disable the bundled copies here to avoid duplicate-extension
        // warnings that break behaviour when a mark is toggled.
        link: false,
        underline: false,
      }),
      Underline,
      TextStyle,
      Color.configure({ types: ['textStyle'] }),
      FontSize,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({ openOnClick: false, HTMLAttributes: { target: '_blank', rel: 'noopener nofollow' } }),
      // Custom image node — extends the stock @tiptap/extension-image with
      // an explicit `title` attribute so captions survive save/load.
      ImageWithTitle.configure({ inline: false, allowBase64: false }),
      Table.configure({ resizable: true, HTMLAttributes: { class: 'indus-table' } }),
      TableRow,
      TableHeader,
      TableCell,
      TaskList.configure({ HTMLAttributes: { class: 'indus-task-list' } }),
      TaskItem.configure({ nested: true }),
      Superscript,
      Subscript,
    ],
    content: value || '<p></p>',
    editorProps: {
      attributes: {
        class: 'prose prose-neutral max-w-none min-h-[300px] focus:outline-none p-4 indus-prose',
      },
      // Double-click on an image opens the alt/caption editor so staff
      // can update SEO metadata without having to delete + reinsert.
      handleDoubleClickOn: (view, pos, node, nodePos) => {
        if (node?.type?.name === 'image') {
          openImageDialogForEdit(node.attrs || {}, nodePos);
          return true; // stop other handlers (e.g. opening in a new tab)
        }
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      lastValueRef.current = html;
      onChange?.(html);
    },
    // Force re-render on selection change so toolbar `active` states update
    // (dropdowns need to reflect current heading level, colour, alignment, etc.).
    onSelectionUpdate: () => forceUpdate((n) => n + 1),
    onTransaction: () => forceUpdate((n) => n + 1),
  });

  // Sync external `value` into the editor. This fires whenever the parent
  // passes a NEW value that didn't come from the editor itself — e.g. when
  // the AdminBlogEditor page finishes loading an existing post asynchronously
  // AFTER the editor has already mounted with an empty string. Without this,
  // opening a published post for edit would render an empty greyed-out body.
  React.useEffect(() => {
    if (!editor) return;
    const incoming = value || '';
    if (incoming === lastValueRef.current) return;
    editor.commands.setContent(incoming || '<p></p>', false);
    lastValueRef.current = incoming;
  }, [editor, value]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL (leave blank to remove link)', previous || 'https://');
    if (url === null) return;
    if (url === '') { editor.chain().focus().extendMarkRange('link').unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  // Image insertion is now a dialog-driven flow: user chooses upload or
  // URL, sets alt text (SEO + a11y) and an optional caption/title tooltip.
  const insertImage = openImageDialogForInsert;

  // Called from inside the dialog when the user picks a file. We upload,
  // capture the returned public_url, and — critically — pre-fill the
  // `alt` field from the original filename if the field is still empty.
  const handleImageFile = async (file) => {
    if (!file) return;
    setImgDlg(d => ({ ...d, uploading: true }));
    try {
      const res = await uploadFile(file, 'image', { context: 'cms_image' });
      const url = res.public_url?.startsWith('http')
        ? res.public_url
        : `${process.env.REACT_APP_BACKEND_URL || ''}${res.public_url}`;
      setImgDlg(d => ({
        ...d,
        src: url,
        // Only prefill alt if the field is empty — never overwrite what
        // the user has already typed (edit mode).
        alt: d.alt || altFromFilename(file.name),
        uploading: false,
      }));
    } catch (e) {
      toast.error('Image upload failed');
      setImgDlg(d => ({ ...d, uploading: false }));
    }
  };

  const closeImageDialog = () => setImgDlg(d => ({ ...d, open: false }));

  const applyImageDialog = () => {
    if (!editor) return;
    const src = (imgDlg.src || '').trim();
    if (!src) { toast.error('Add an image URL or upload a file first'); return; }
    const alt = (imgDlg.alt || '').trim();
    const title = (imgDlg.title || '').trim() || null;
    // If alt is empty, warn once but still allow — Google prefers omitting
    // alt over stuffing it, and our backend `fill_html_image_alts` will
    // fallback to focus_keyword on save.
    if (!alt) {
      toast.warning('No alt text provided — one will be auto-filled from the focus keyword on save.');
    }

    if (imgDlg.mode === 'edit' && imgDlg.pos != null) {
      // Update in place — preserves position, selection restores after.
      editor.chain().focus().command(({ tr, state }) => {
        const node = state.doc.nodeAt(imgDlg.pos);
        if (!node || node.type.name !== 'image') return false;
        tr.setNodeMarkup(imgDlg.pos, undefined, {
          ...node.attrs, src, alt: alt || null, title,
        });
        return true;
      }).run();
      toast.success('Image updated');
    } else {
      editor.chain().focus().setImage({ src, alt: alt || null, title }).run();
      toast.success('Image inserted');
    }
    closeImageDialog();
  };

  if (!editor) {
    return <div className='border rounded-lg p-4 text-muted-foreground text-sm'>Loading editor…</div>;
  }

  // Derive the current heading label for the dropdown trigger.
  const activeHeadingLabel = (() => {
    for (const h of HEADINGS) {
      if (h.level === 0 && editor.isActive('paragraph')) return h.label;
      if (h.level > 0 && editor.isActive('heading', { level: h.level })) return h.label;
    }
    return 'Paragraph';
  })();

  const setHeading = (level) => {
    if (level === 0) editor.chain().focus().setParagraph().run();
    else editor.chain().focus().toggleHeading({ level }).run();
  };

  // Insert an arbitrary R×C table. Clamped to sane bounds so a typo in the
  // custom inputs can't lock the editor up building thousands of cells.
  const insertTable = (rows, cols) => {
    const r = Math.max(1, Math.min(50, Number(rows) || 0));
    const c = Math.max(1, Math.min(20, Number(cols) || 0));
    if (!r || !c) return;
    editor.chain().focus().insertTable({ rows: r, cols: c, withHeaderRow: tblHeader }).run();
    setTableMenuOpen(false);
    setTblHover({ rows: 0, cols: 0 });
  };

  const currentColor = editor.getAttributes('textStyle').color || null;
  const currentHighlight = editor.getAttributes('highlight').color || null;

  return (
    <div className='border rounded-lg bg-background' data-testid='rich-text-editor'>
      {/* Toolbar — sticky so it stays pinned while the user scrolls the whole
          page through a long article. `top-16` matches the global header
          height (h-16 in Header.jsx) so the toolbar sits just below it and
          never disappears under the header. `rounded-t-lg` re-adds the
          rounded top corners (we removed `overflow-hidden` on the parent
          because it kills `position: sticky`). */}
      <div data-testid='rte-toolbar' className='flex flex-wrap items-center gap-1 p-2 border-b bg-secondary/95 backdrop-blur supports-[backdrop-filter]:bg-secondary/80 sticky top-16 z-20 rounded-t-lg shadow-sm'>
        {/* Headings & paragraph selector */}
        <BtnGroup>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type='button'
                className='h-8 min-w-[110px] px-2 rounded-md inline-flex items-center justify-between gap-1 text-xs font-medium hover:bg-secondary text-foreground border border-border/50 bg-background'
                data-testid='rte-heading-trigger'
                title='Text style'
              >
                <span className='inline-flex items-center gap-1'><Type className='h-3.5 w-3.5' /> {activeHeadingLabel}</span>
                <ChevronDown className='h-3.5 w-3.5 opacity-60' />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='start' className='w-44' data-testid='rte-heading-menu'>
              {HEADINGS.map((h) => (
                <DropdownMenuItem
                  key={h.level}
                  onClick={() => setHeading(h.level)}
                  className='cursor-pointer'
                  data-testid={`rte-heading-${h.level}`}
                >
                  {h.level === 0 ? (
                    <span className='text-sm'>Paragraph</span>
                  ) : (
                    <span className={
                      h.level === 1 ? 'text-xl font-bold' :
                      h.level === 2 ? 'text-lg font-bold' :
                      h.level === 3 ? 'text-base font-semibold' :
                      h.level === 4 ? 'text-sm font-semibold' :
                      'text-xs font-semibold'
                    }>{h.label}</span>
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Font size */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type='button'
                className='h-8 px-2 rounded-md inline-flex items-center gap-0.5 text-xs font-medium hover:bg-secondary text-muted-foreground border border-transparent'
                title='Font size'
                data-testid='rte-fontsize-trigger'
              >
                <span>Size</span>
                <ChevronDown className='h-3 w-3 opacity-60' />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='start' data-testid='rte-fontsize-menu'>
              {FONT_SIZES.map((s) => (
                <DropdownMenuItem
                  key={s.label}
                  onClick={() => {
                    if (s.value === null) editor.chain().focus().unsetFontSize().run();
                    else editor.chain().focus().setFontSize(s.value).run();
                  }}
                  className='cursor-pointer'
                  data-testid={`rte-fontsize-${s.label.toLowerCase()}`}
                >
                  <span style={{ fontSize: s.value || '1em' }}>{s.label}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </BtnGroup>

        {/* Inline formatting marks */}
        <BtnGroup>
          <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title='Bold (Ctrl+B)' testid='rte-bold'><Bold className='h-4 w-4' /></Btn>
          <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title='Italic (Ctrl+I)' testid='rte-italic'><Italic className='h-4 w-4' /></Btn>
          <Btn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title='Underline (Ctrl+U)' testid='rte-underline'><UnderlineIcon className='h-4 w-4' /></Btn>
          <Btn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title='Strikethrough' testid='rte-strike'><Strikethrough className='h-4 w-4' /></Btn>
        </BtnGroup>

        {/* Text colour + highlight */}
        <BtnGroup>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type='button'
                className={`h-8 w-8 rounded-md inline-flex items-center justify-center hover:bg-secondary text-muted-foreground relative`}
                title='Text colour'
                data-testid='rte-color-trigger'
              >
                <Palette className='h-4 w-4' />
                {currentColor && (
                  <span
                    className='absolute bottom-1 left-1.5 right-1.5 h-1 rounded-sm'
                    style={{ background: currentColor }}
                  />
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='start' data-testid='rte-color-menu'>
              <div className='text-[10px] uppercase tracking-wider text-muted-foreground px-2 pt-1'>Text colour</div>
              <ColorSwatchGrid
                palette={PALETTE}
                current={currentColor}
                onPick={(c) => {
                  if (c === null) editor.chain().focus().unsetColor().run();
                  else editor.chain().focus().setColor(c).run();
                }}
              />
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type='button'
                className={`h-8 w-8 rounded-md inline-flex items-center justify-center hover:bg-secondary text-muted-foreground relative`}
                title='Highlight'
                data-testid='rte-highlight-trigger'
              >
                <Highlighter className='h-4 w-4' />
                {currentHighlight && (
                  <span
                    className='absolute bottom-1 left-1.5 right-1.5 h-1 rounded-sm'
                    style={{ background: currentHighlight }}
                  />
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='start' data-testid='rte-highlight-menu'>
              <div className='text-[10px] uppercase tracking-wider text-muted-foreground px-2 pt-1'>Highlight</div>
              <ColorSwatchGrid
                palette={HIGHLIGHT_PALETTE}
                current={currentHighlight}
                onPick={(c) => {
                  if (c === null) editor.chain().focus().unsetHighlight().run();
                  else editor.chain().focus().setHighlight({ color: c }).run();
                }}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        </BtnGroup>

        {/* Alignment */}
        <BtnGroup>
          <Btn onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title='Align left' testid='rte-align-left'><AlignLeft className='h-4 w-4' /></Btn>
          <Btn onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title='Align centre' testid='rte-align-center'><AlignCenter className='h-4 w-4' /></Btn>
          <Btn onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title='Align right' testid='rte-align-right'><AlignRight className='h-4 w-4' /></Btn>
          <Btn onClick={() => editor.chain().focus().setTextAlign('justify').run()} active={editor.isActive({ textAlign: 'justify' })} title='Justify' testid='rte-align-justify'><AlignJustify className='h-4 w-4' /></Btn>
        </BtnGroup>

        {/* Lists & blocks */}
        <BtnGroup>
          <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title='Bullet list' testid='rte-ul'><List className='h-4 w-4' /></Btn>
          <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title='Numbered list' testid='rte-ol'><ListOrdered className='h-4 w-4' /></Btn>
          <Btn onClick={() => editor.chain().focus().toggleTaskList().run()} active={editor.isActive('taskList')} title='Task list (checkboxes)' testid='rte-tasklist'><ListChecks className='h-4 w-4' /></Btn>
          <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title='Blockquote' testid='rte-quote'><Quote className='h-4 w-4' /></Btn>
          <Btn onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} title='Code block' testid='rte-code'><Code2 className='h-4 w-4' /></Btn>
          <Btn onClick={() => editor.chain().focus().setHorizontalRule().run()} title='Horizontal rule' testid='rte-hr'><Minus className='h-4 w-4' /></Btn>
        </BtnGroup>

        {/* Super/subscript */}
        <BtnGroup>
          <Btn onClick={() => editor.chain().focus().toggleSuperscript().run()} active={editor.isActive('superscript')} title='Superscript' testid='rte-superscript'><SuperIcon className='h-4 w-4' /></Btn>
          <Btn onClick={() => editor.chain().focus().toggleSubscript().run()} active={editor.isActive('subscript')} title='Subscript' testid='rte-subscript'><SubIcon className='h-4 w-4' /></Btn>
        </BtnGroup>

        {/* Tables */}
        <BtnGroup>
          <DropdownMenu open={tableMenuOpen} onOpenChange={setTableMenuOpen}>
            <DropdownMenuTrigger asChild>
              <button
                type='button'
                className='h-8 w-8 rounded-md inline-flex items-center justify-center hover:bg-secondary text-muted-foreground'
                title='Table'
                data-testid='rte-table-trigger'
              >
                <TableIcon className='h-4 w-4' />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='start' data-testid='rte-table-menu'>
              {/* Size picker — hover to preview, click to insert any R×C table */}
              <div className='px-2 pt-1.5 pb-2' data-testid='rte-table-size-picker'>
                <div className='text-[11px] text-muted-foreground mb-1.5' data-testid='rte-table-size-label'>
                  {tblHover.rows > 0
                    ? `${tblHover.rows} × ${tblHover.cols} table`
                    : 'Insert table — pick a size'}
                </div>
                <div
                  className='inline-grid gap-[2px]'
                  style={{ gridTemplateColumns: `repeat(${TABLE_GRID_COLS}, 14px)` }}
                  onMouseLeave={() => setTblHover({ rows: 0, cols: 0 })}
                >
                  {Array.from({ length: TABLE_GRID_ROWS * TABLE_GRID_COLS }).map((_c, i) => {
                    const r = Math.floor(i / TABLE_GRID_COLS) + 1;
                    const c = (i % TABLE_GRID_COLS) + 1;
                    const on = r <= tblHover.rows && c <= tblHover.cols;
                    return (
                      <button
                        key={i}
                        type='button'
                        aria-label={`${r} by ${c} table`}
                        className={`h-[14px] w-[14px] rounded-[2px] border transition-colors ${on ? 'bg-primary border-primary' : 'bg-background border-border hover:border-primary/60'}`}
                        onMouseEnter={() => setTblHover({ rows: r, cols: c })}
                        onFocus={() => setTblHover({ rows: r, cols: c })}
                        onClick={() => insertTable(r, c)}
                        data-testid={`rte-table-cell-${r}-${c}`}
                      />
                    );
                  })}
                </div>
                {/* Custom size for tables larger than the grid */}
                <div className='flex items-end gap-1.5 mt-2'>
                  <div>
                    <div className='text-[10px] text-muted-foreground'>Rows</div>
                    <Input
                      type='number'
                      min={1}
                      max={50}
                      value={tblCustom.rows}
                      onChange={(e) => setTblCustom(s => ({ ...s, rows: e.target.value }))}
                      className='h-7 w-14 px-1.5 text-xs'
                      data-testid='rte-table-rows-input'
                    />
                  </div>
                  <div>
                    <div className='text-[10px] text-muted-foreground'>Cols</div>
                    <Input
                      type='number'
                      min={1}
                      max={20}
                      value={tblCustom.cols}
                      onChange={(e) => setTblCustom(s => ({ ...s, cols: e.target.value }))}
                      className='h-7 w-14 px-1.5 text-xs'
                      data-testid='rte-table-cols-input'
                    />
                  </div>
                  <Button
                    type='button'
                    size='sm'
                    className='h-7 text-xs'
                    onClick={() => insertTable(tblCustom.rows, tblCustom.cols)}
                    data-testid='rte-table-insert'
                  >
                    Insert
                  </Button>
                </div>
                <label className='flex items-center gap-1.5 mt-2 text-[11px] text-muted-foreground cursor-pointer'>
                  <input
                    type='checkbox'
                    checked={tblHeader}
                    onChange={(e) => setTblHeader(e.target.checked)}
                    className='h-3 w-3 accent-current'
                    data-testid='rte-table-header-row-toggle'
                  />
                  Include header row
                </label>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => editor.chain().focus().addRowAfter().run()} disabled={!editor.can().addRowAfter()} className='cursor-pointer'>
                <Rows3 className='h-4 w-4 mr-2' /> Add row below
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => editor.chain().focus().addColumnAfter().run()} disabled={!editor.can().addColumnAfter()} className='cursor-pointer'>
                <Columns3 className='h-4 w-4 mr-2' /> Add column right
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => editor.chain().focus().deleteRow().run()} disabled={!editor.can().deleteRow()} className='cursor-pointer'>
                <Trash2 className='h-4 w-4 mr-2' /> Delete row
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => editor.chain().focus().deleteColumn().run()} disabled={!editor.can().deleteColumn()} className='cursor-pointer'>
                <Trash2 className='h-4 w-4 mr-2' /> Delete column
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => editor.chain().focus().toggleHeaderRow().run()} disabled={!editor.can().toggleHeaderRow()} className='cursor-pointer'>
                Toggle header row
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => editor.chain().focus().deleteTable().run()} disabled={!editor.can().deleteTable()} className='cursor-pointer text-destructive'>
                <Trash2 className='h-4 w-4 mr-2' /> Delete table
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </BtnGroup>

        {/* Insertables */}
        <BtnGroup>
          <Btn onClick={setLink} active={editor.isActive('link')} title='Insert link' testid='rte-link'><LinkIcon className='h-4 w-4' /></Btn>
          <Btn onClick={insertImage} title='Insert image' testid='rte-image'><ImageIcon className='h-4 w-4' /></Btn>
        </BtnGroup>

        {/* Clear formatting */}
        <BtnGroup>
          <Btn
            onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
            title='Clear formatting'
            testid='rte-clear-formatting'
          >
            <Eraser className='h-4 w-4' />
          </Btn>
        </BtnGroup>

        {/* Undo/redo */}
        <BtnGroup>
          <Btn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title='Undo' testid='rte-undo'><Undo2 className='h-4 w-4' /></Btn>
          <Btn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title='Redo' testid='rte-redo'><Redo2 className='h-4 w-4' /></Btn>
        </BtnGroup>
      </div>
      <EditorContent editor={editor} />

      {/* Image insert / edit dialog — captures alt (SEO + a11y) and an
          optional caption stored as the img `title` attribute (tooltip). */}
      <Dialog open={imgDlg.open} onOpenChange={(o) => setImgDlg(d => ({ ...d, open: o }))}>
        <DialogContent className='sm:max-w-md' data-testid='rte-image-dialog'>
          <DialogHeader>
            <DialogTitle>{imgDlg.mode === 'edit' ? 'Edit image' : 'Insert image'}</DialogTitle>
            <DialogDescription>
              {imgDlg.mode === 'edit'
                ? 'Update the image, alt text, or caption. Alt text is used for accessibility and image SEO.'
                : 'Upload or paste an image URL, then add alt text so search engines and screen readers can understand it.'}
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-4'>
            {/* Preview */}
            {imgDlg.src && (
              <div className='rounded-md border overflow-hidden bg-muted/40 max-h-48 flex items-center justify-center'>
                <img
                  src={imgDlg.src}
                  alt={imgDlg.alt || 'preview'}
                  className='max-h-48 max-w-full object-contain'
                />
              </div>
            )}

            {/* Upload / URL row */}
            <div>
              <Label className='text-xs'>Image source</Label>
              <div className='flex items-center gap-2 mt-1'>
                <label className='inline-flex items-center gap-1.5 rounded-md border px-3 py-2 cursor-pointer text-sm hover:bg-secondary shrink-0'>
                  <Upload className='h-4 w-4' />
                  {imgDlg.uploading ? 'Uploading…' : (imgDlg.src ? 'Replace' : 'Upload')}
                  <input
                    type='file'
                    accept='image/*'
                    className='hidden'
                    disabled={imgDlg.uploading}
                    data-testid='rte-image-file-input'
                    onChange={(e) => { handleImageFile(e.target.files?.[0]); e.target.value = ''; }}
                  />
                </label>
                <div className='flex items-center gap-2 flex-1'>
                  <Link2 className='h-4 w-4 text-muted-foreground shrink-0' />
                  <Input
                    placeholder='https://…'
                    value={imgDlg.src}
                    onChange={(e) => setImgDlg(d => ({ ...d, src: e.target.value }))}
                    data-testid='rte-image-src-input'
                  />
                </div>
              </div>
            </div>

            {/* Alt text */}
            <div>
              <Label className='text-xs' htmlFor='rte-image-alt-input'>
                Alt text <span className='text-primary'>(SEO &amp; accessibility)</span>
              </Label>
              <Input
                id='rte-image-alt-input'
                value={imgDlg.alt}
                onChange={(e) => setImgDlg(d => ({ ...d, alt: e.target.value }))}
                placeholder='e.g. Used HAAS VF-2 CNC milling machine, control panel view'
                maxLength={200}
                data-testid='rte-image-alt-input'
              />
              <div className='text-[11px] text-muted-foreground mt-1'>
                Describe what the image shows in one short sentence. Pre-filled from the filename. {(imgDlg.alt || '').length}/200
              </div>
            </div>

            {/* Caption / title (tooltip) */}
            <div>
              <Label className='text-xs' htmlFor='rte-image-title-input'>
                Caption <span className='text-muted-foreground'>(optional — shown as tooltip)</span>
              </Label>
              <Input
                id='rte-image-title-input'
                value={imgDlg.title}
                onChange={(e) => setImgDlg(d => ({ ...d, title: e.target.value }))}
                placeholder='e.g. Photo credit — courtesy of the seller'
                maxLength={300}
                data-testid='rte-image-title-input'
              />
            </div>
          </div>

          <DialogFooter className='gap-2 sm:gap-2'>
            <Button
              type='button'
              variant='outline'
              onClick={closeImageDialog}
              data-testid='rte-image-cancel'
            >Cancel</Button>
            <Button
              type='button'
              onClick={applyImageDialog}
              disabled={imgDlg.uploading || !(imgDlg.src || '').trim()}
              data-testid='rte-image-apply'
            >
              {imgDlg.mode === 'edit' ? 'Save changes' : 'Insert image'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RichTextEditor;
